import * as React from "react";
import { X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

export interface ChipFilterOption {
  value: string;
  label: string;
}

interface ChipFilterProps {
  label: string;
  options: ChipFilterOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onRemove: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  multiSelect?: boolean;
  icon?: React.ReactNode;
}

export function ChipFilter({
  label,
  options,
  value,
  onChange,
  onRemove,
  open,
  onOpenChange,
  multiSelect = false,
  icon,
}: ChipFilterProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const resolvedOpen = open ?? internalOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [open, onOpenChange],
  );

  // Normalize value to array for internal logic
  const selectedValues: string[] = Array.isArray(value) ? value : [value];
  const isAll = selectedValues.includes("all") || selectedValues.length === 0;

  // Build chip label
  let chipLabel: string;
  if (isAll) {
    chipLabel = label;
  } else if (selectedValues.length === 1) {
    const opt = options.find((o) => o.value === selectedValues[0]);
    chipLabel = opt ? opt.label : label;
  } else {
    chipLabel = `${label} (${selectedValues.length})`;
  }

  const handleToggle = (optionValue: string) => {
    if (!multiSelect) {
      // Single-select: just set value directly
      onChange(optionValue);
      return;
    }

    // Multi-select toggle logic
    if (isAll) {
      // Going from "all" to selecting one item
      onChange([optionValue]);
    } else if (selectedValues.includes(optionValue)) {
      // Deselect: if last item, reset to all
      const next = selectedValues.filter((v) => v !== optionValue);
      onChange(next.length === 0 ? ["all"] : next);
    } else {
      // Add to selection
      onChange([...selectedValues, optionValue]);
    }
  };

  const handleReset = () => {
    onChange(multiSelect ? ["all"] : "all");
  };

  return (
    <div className="flex items-center">
      <Popover open={resolvedOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={
              isAll
                ? "gap-1.5 h-9 text-sm"
                : "gap-1.5 h-9 text-sm border-primary bg-primary/10 text-primary dark:text-[hsl(var(--aurora-tx-link))] hover:bg-primary/15 hover:text-primary dark:hover:text-[hsl(var(--aurora-tx-link))]"
            }
          >
            {icon && (
              <span
                className={
                  isAll
                    ? "text-muted-foreground flex items-center"
                    : "flex items-center text-current"
                }
              >
                {icon}
              </span>
            )}
            {chipLabel}
            <span
              role="button"
              tabIndex={0}
              className={
                isAll
                  ? "ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                  : "ml-0.5 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
              }
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onRemove();
                }
              }}
            >
              <X className="h-3 w-3" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-0" align="start" sideOffset={6}>
          <div className="p-3 space-y-2 max-h-72 overflow-y-auto">
            {options.map((option) => {
              const isChecked = !isAll && selectedValues.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex items-center gap-2 cursor-pointer text-sm hover:text-foreground text-muted-foreground data-[checked=true]:text-foreground"
                  data-checked={isChecked}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => handleToggle(option.value)}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleOpenChange(false)}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
